import React, { useState } from "react";
import {
  Box, TextField, Button, Typography,
  InputAdornment, IconButton, CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff, Lock, Badge } from "@mui/icons-material";
import { motion } from "framer-motion";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useLoginMutation } from "state/api";
import { loginSuccess } from "state/authSlice";
import { deriveRole } from "state/RoleBasedRoute";
import { toast } from "react-toastify";
import LoginHero from "./LoginHero";

/* ── Text-field dark-glass styles ── */
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    color: "#fff",
    background: "rgba(255,255,255,0.055)",
    borderRadius: "12px",
    transition: "background 0.25s",
    "& fieldset": { borderColor: "rgba(255,255,255,0.13)" },
    "&:hover fieldset": { borderColor: "rgba(206,147,216,0.48)" },
    "&.Mui-focused": {
      background: "rgba(255,255,255,0.09)",
      "& fieldset": { borderColor: "#ce93d8", borderWidth: "1.5px" },
    },
  },
  "& .MuiInputLabel-root": {
    color: "rgba(255,255,255,0.36)",
    fontSize: 14,
    "&.Mui-focused": { color: "#ce93d8" },
  },
  "& .MuiInputBase-input": { fontSize: 14, py: "13.5px" },
};

const containerV = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.09, delayChildren: 0.2 } },
};
const itemV = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};

const ROLES = [
  { label: "MR",    color: "#42a5f5" },
  { label: "AM",    color: "#ce93d8" },
  { label: "Admin", color: "#f48fb1" },
];

/* ════════════════════════════════════════════
   COMPONENT
════════════════════════════════════════════ */
const Login = () => {
  const [EmpNo, setEmpNo]               = useState("");
  const [password, setPassword]         = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [login, { isLoading }]          = useLoginMutation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res  = await login({ EmpNo, password }).unwrap();
      dispatch(loginSuccess({ user: res.user, token: res.token }));
      const role = deriveRole(res.user.role);
      toast.success(`Welcome, ${res.user.username}!`);
      if (role === "MR")         navigate("/mr/dashboard");
      else if (role === "AM")    navigate("/am/dashboard");
      else if (role === "Admin") navigate("/admin/dashboard");
    } catch (err) {
      toast.error(err?.data?.message || "Login failed");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        background: "linear-gradient(140deg,#0d001f 0%,#160030 45%,#25004a 100%)",
        overflow: "hidden",
        position: "relative",
        fontFamily: "'Inter',sans-serif",
      }}
    >
      {/* ── Ambient orbs ── */}
      {[
        { w:560, h:560, bg:"#7b1fa2", op:0.28, top:"-12%",  left:"-8%",              ay:[-24,24],  ax:[-12,12]  },
        { w:460, h:460, bg:"#e91e63", op:0.22, bottom:"-12%",right:"-6%",            ay:[24,-24],  ax:[12,-12]  },
        { w:280, h:280, bg:"#9c27b0", op:0.16, top:"42%",   left:"36%",             ay:[-18,18],  ax:[-9,9]    },
      ].map(({ w, h, bg, op, top, bottom, left, right, ay, ax }, i) => (
        <motion.div key={i}
          animate={{ y: ay, x: ax }}
          transition={{ duration: 9 + i * 2, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
          style={{
            position: "absolute", borderRadius: "50%",
            width: w, height: h, background: bg, opacity: op,
            filter: "blur(72px)", pointerEvents: "none",
            top, bottom, left, right,
          }}
        />
      ))}

      {/* ════ LEFT — Pharmaceutical animation ════ */}
      <Box sx={{ flex: 1, display: { xs: "none", md: "block" }, position: "relative", minHeight: "100vh", zIndex: 1 }}>
        <LoginHero />
      </Box>

      {/* ════ RIGHT — Login form ════ */}
      <Box
        sx={{
          width: { xs: "100%", md: 500 },
          display: "flex", alignItems: "center", justifyContent: "center",
          p: { xs: 3, sm: 5, md: 6 },
          position: "relative", zIndex: 2,
        }}
      >
        <motion.div
          initial={{ opacity: 0, x: 52, scale: 0.96 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
          style={{ width: "100%", maxWidth: 400 }}
        >
          {/* ── Animated glow card ── */}
          <Box
            sx={{
              background: "rgba(255,255,255,0.062)",
              backdropFilter: "blur(30px) saturate(180%)",
              border: "1px solid rgba(255,255,255,0.11)",
              borderRadius: "26px",
              p: { xs: 4, sm: 5 },
              "@keyframes cardGlow": {
                "0%,100%": { boxShadow: "0 0 0 1px rgba(156,39,176,0.42), 0 44px 90px rgba(0,0,0,0.6)" },
                "33%":     { boxShadow: "0 0 0 1px rgba(66,165,245,0.42),  0 44px 90px rgba(0,0,0,0.6)" },
                "66%":     { boxShadow: "0 0 0 1px rgba(233,30,99,0.42),   0 44px 90px rgba(0,0,0,0.6)" },
              },
              animation: "cardGlow 5s ease-in-out infinite",
            }}
          >
            {/* Mobile logo */}
            <Box sx={{ display: { xs: "flex", md: "none" }, alignItems: "center", justifyContent: "center", mb: 4 }}>
              <Box sx={{ position: "relative", mr: 1.5 }}>
                <motion.div
                  animate={{ scale: [1, 1.5, 1], opacity: [0.65, 0, 0.65] }}
                  transition={{ duration: 2.6, repeat: Infinity, ease: "easeOut" }}
                  style={{ position: "absolute", inset: -7, borderRadius: "18px",
                    border: "2px solid rgba(233,30,99,0.52)", pointerEvents: "none" }}
                />
                <Box sx={{ width: 50, height: 50, borderRadius: "15px",
                  background: "linear-gradient(135deg,#9c27b0,#e91e63)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  boxShadow: "0 6px 22px rgba(233,30,99,0.42)" }}>
                  <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: 22 }}>N</Typography>
                </Box>
              </Box>
              <Box>
                <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: 23, lineHeight: 1 }}>NAARI</Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.36)", fontSize: 10, letterSpacing: 2.5, textTransform: "uppercase" }}>
                  Jagsonpal
                </Typography>
              </Box>
            </Box>

            <motion.div initial="hidden" animate="visible" variants={containerV}>

              {/* ── Logo row (desktop) ── */}
              <motion.div variants={itemV}>
                <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", mb: 3.5 }}>
                  <Box sx={{ position: "relative", mr: 1.8 }}>
                    <motion.div
                      animate={{ scale: [1, 1.55, 1], opacity: [0.6, 0, 0.6] }}
                      transition={{ duration: 2.6, repeat: Infinity, ease: "easeOut" }}
                      style={{ position: "absolute", inset: -7, borderRadius: "18px",
                        border: "2px solid rgba(233,30,99,0.5)", pointerEvents: "none" }}
                    />
                    <Box sx={{ width: 50, height: 50, borderRadius: "15px",
                      background: "linear-gradient(135deg,#9c27b0,#e91e63)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      boxShadow: "0 6px 22px rgba(233,30,99,0.42)" }}>
                      <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: 22 }}>N</Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: 22, lineHeight: 1, letterSpacing: -0.3 }}>NAARI</Typography>
                    <Typography sx={{ color: "rgba(255,255,255,0.35)", fontSize: 10, letterSpacing: 2.5, textTransform: "uppercase", mt: 0.3 }}>
                      Jagsonpal
                    </Typography>
                  </Box>
                </Box>
              </motion.div>

              {/* ── Heading ── */}
              <motion.div variants={itemV}>
                <Typography sx={{
                  fontWeight: 800, fontSize: 26, mb: 0.5, letterSpacing: -0.4,
                  background: "linear-gradient(135deg,#fff 0%,#ce93d8 55%,#f48fb1 100%)",
                  WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                }}>
                  Welcome back
                </Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: 13.5, mb: 2.5, lineHeight: 1.55 }}>
                  Sign in with your employee credentials
                </Typography>
              </motion.div>

              {/* ── Role chips ── */}
              <motion.div variants={itemV}>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3.5 }}>
                  {ROLES.map(({ label, color }) => (
                    <Box key={label} sx={{
                      px: 1.4, py: 0.45, borderRadius: "100px",
                      background: "rgba(255,255,255,0.06)",
                      border: "1px solid rgba(255,255,255,0.09)",
                      display: "flex", alignItems: "center", gap: 0.7,
                    }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: color, boxShadow: `0 0 6px ${color}` }}/>
                      <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: 11.5, fontWeight: 500 }}>{label}</Typography>
                    </Box>
                  ))}
                </Box>
              </motion.div>

              <form onSubmit={handleSubmit} autoComplete="off">

                {/* Employee No */}
                <motion.div variants={itemV}>
                  <TextField fullWidth label="Employee No" value={EmpNo}
                    onChange={(e) => setEmpNo(e.target.value)}
                    required autoComplete="username"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Badge sx={{ color: "rgba(255,255,255,0.28)", fontSize: 18 }}/>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 2.5, ...fieldSx }}
                  />
                </motion.div>

                {/* Password */}
                <motion.div variants={itemV}>
                  <TextField fullWidth label="Password"
                    type={showPassword ? "text" : "password"}
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    required autoComplete="current-password"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock sx={{ color: "rgba(255,255,255,0.28)", fontSize: 18 }}/>
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(v => !v)} edge="end"
                            aria-label={showPassword ? "Hide password" : "Show password"}
                            sx={{ color: "rgba(255,255,255,0.28)", mr: -0.5,
                              "&:hover": { color: "#ce93d8", background: "rgba(206,147,216,0.1)" },
                              transition: "color 0.2s" }}>
                            {showPassword ? <VisibilityOff sx={{ fontSize: 18 }}/> : <Visibility sx={{ fontSize: 18 }}/>}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 4, ...fieldSx }}
                  />
                </motion.div>

                {/* Submit with shimmer */}
                <motion.div variants={itemV}>
                  <motion.div
                    whileHover={!isLoading ? { scale: 1.025 } : {}}
                    whileTap={!isLoading  ? { scale: 0.975 } : {}}
                    transition={{ duration: 0.18 }}
                  >
                    <Box sx={{ position: "relative", overflow: "hidden", borderRadius: "12px" }}>
                      <Button fullWidth type="submit" variant="contained" size="large"
                        disabled={isLoading}
                        sx={{
                          background: "linear-gradient(135deg,#9c27b0 0%,#c2185b 60%,#e91e63 100%)",
                          color: "#fff", fontWeight: 700, fontSize: 15,
                          py: "13.5px", borderRadius: "12px", textTransform: "none",
                          letterSpacing: 0.3, border: "none",
                          boxShadow: "0 8px 28px rgba(233,30,99,0.42), 0 1px 0 rgba(255,255,255,0.15) inset",
                          transition: "box-shadow 0.25s,background 0.25s",
                          "&:hover": {
                            background: "linear-gradient(135deg,#ab47bc 0%,#d81b60 60%,#f06292 100%)",
                            boxShadow: "0 14px 40px rgba(233,30,99,0.58), 0 1px 0 rgba(255,255,255,0.15) inset",
                          },
                          "&:disabled": { background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.28)", boxShadow: "none" },
                        }}>
                        {isLoading ? (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                            <CircularProgress size={17} sx={{ color: "rgba(255,255,255,0.65)" }}/>
                            <span>Signing in…</span>
                          </Box>
                        ) : "Sign In"}
                      </Button>

                      {/* Shimmer sweep */}
                      {!isLoading && (
                        <motion.div
                          animate={{ x: ["-120%", "120%"] }}
                          transition={{ duration: 2.8, repeat: Infinity, ease: "linear", repeatDelay: 1.2 }}
                          style={{
                            position: "absolute", top: 0, bottom: 0, width: "38%",
                            background: "linear-gradient(90deg,transparent,rgba(255,255,255,0.16),transparent)",
                            pointerEvents: "none",
                          }}
                        />
                      )}
                    </Box>
                  </motion.div>
                </motion.div>

              </form>
            </motion.div>

            {/* Footer */}
            <Box sx={{ mt: 4.5, textAlign: "center" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mb: 1 }}>
                <Box sx={{ flex: 1, height: "1px", background: "rgba(255,255,255,0.07)" }}/>
                <Typography sx={{ color: "rgba(255,255,255,0.18)", fontSize: 11, px: 1 }}>secured</Typography>
                <Box sx={{ flex: 1, height: "1px", background: "rgba(255,255,255,0.07)" }}/>
              </Box>
              <Typography sx={{ color: "rgba(255,255,255,0.18)", fontSize: 11, letterSpacing: 0.4, lineHeight: 1.65 }}>
                © 2025 Jagsonpal Pharmaceuticals Ltd.
                <br/>NAARI Field Force Tracker · v1.0
              </Typography>
            </Box>
          </Box>
        </motion.div>
      </Box>
    </Box>
  );
};

export default Login;
